/**
 * Ribbon mesh builder: a direct port of the Burst jobs in
 * vendor-ref/nanome/Assets/Scripts/src/Nanome.Infrastructure/Display/EntryRendering/RibbonBuilder.cs
 * (FillGVectorJob :320-347, ControlPointsInitializationJob :350-422, ComputeWidthHeightJob :425-589,
 * GenerateMeshJob :592-716, AddNucleicBases :835-1229, GetPlaneNormal :1231-1240).
 *
 * Coordinates are the model's own frame (Å, right-handed, as the file gives them). Nanome converts to its
 * Unity scene frame by swapping y and z (Adapter/Math/Conversion.cs:8-11), a reflection; the renderer applies
 * the proper rotation gl = (x, z, -y) instead, so computing here in the domain frame with the same index order
 * yields the same visible faces (counter-clockwise outward here == Unity's clockwise front faces after the swap).
 *
 * Pure functions over typed arrays, no DOM, deterministic: safe inside a worker.
 */
import { RIBBON } from '@nanome-lite/look';
import { CartoonStyle, type RibbonBuildOptions, type RibbonMeshBuffers, type RibbonSegment } from './types';

/** Unity.Mathematics math.normalizesafe threshold: FLT_MIN_NORMAL = 1.175494351e-38 on dot(x, x). */
const FLT_MIN_NORMAL = 1.175494351e-38;

/** Width/height constants (RibbonBuilder.cs:124-125, 355) via the look spec; never retyped here. */
const HELIX_HALF_WIDTH = RIBBON.helixHalfWidth;     // Width * Radius = 6.0 * 0.15 (RibbonBuilder.cs:386, 392)
const HELIX_HALF_HEIGHT = RIBBON.helixHalfHeight;   // Radius / 2 (RibbonBuilder.cs:386, 392, 410)
const ARROW_HALF_WIDTH = RIBBON.arrowHalfWidth;     // Width * Radius * 1.75 (RibbonBuilder.cs:410), stored negative as the head flag
const COIL_HALF_SIZE = RIBBON.coilHalfSize;         // Radius * Size = 0.15 * 0.85 (RibbonBuilder.cs:418)
const BASE_HEIGHT = RIBBON.nucleicBaseHalfSize;     // const float height = 0.2f (RibbonBuilder.cs:837)
/** _bSpline rows = (-1,3,-3,1)/6, (3,-6,3,0)/6, (-3,0,3,0)/6, (1,4,1,0)/6 (RibbonBuilder.cs:132-135); the look table stores the integer rows. */
type Row4 = readonly [number, number, number, number];
const BSPLINE: readonly [Row4, Row4, Row4, Row4] = RIBBON.bsplineBasis.map((row) => row.map((x) => x / 6) as unknown as Row4) as unknown as [Row4, Row4, Row4, Row4];

/** Per-residue base-ring layout inside RibbonSegment.baseRings[i] (see segments.ts): */
export const PURINE_RING_FLOATS = 33;     // ring1 N9,C4,C5,N7,C8 + ring2 C4,N3,C2,N1,C6,C5 (RenderingViewModel.Ribbon.cs:754-770)
export const PYRIMIDINE_RING_FLOATS = 18; // ring1 N1,C2,N3,C4,C5,C6 (RenderingViewModel.Ribbon.cs:778-786)

/** Per-point layout of the tube mesh (GenerateMeshJob): 4 vertices, 24 indices per point except the last, which writes the 12 cap indices. */
export const TUBE_VERTS_PER_POINT = 4;
export const TUBE_INDICES_PER_POINT = 24;
export const TUBE_CAP_INDICES = 12;

/** Vertex / index counts of one nucleic base (stick RibbonBuilder.cs:875-949, purine slab :964-1130, pyrimidine slab :1131-1219). */
export const BASE_STICK_VERTS = 8, BASE_STICK_INDICES = 30;
export const PURINE_SLAB_VERTS = 18, PURINE_SLAB_INDICES = 96;
export const PYRIMIDINE_SLAB_VERTS = 12, PYRIMIDINE_SLAB_INDICES = 60;

/** Exact buffer sizes buildRibbon produces for a segment, so callers can budget before building. */
export function ribbonBufferSizes(segment: RibbonSegment, options: RibbonBuildOptions): { vertexCount: number; indexCount: number } {
  const n = segment.residueIndex.length;
  if (n < 2) return { vertexCount: 0, indexCount: 0 };
  const totalPoints = options.stepsPerResidue * n;
  let vertexCount = TUBE_VERTS_PER_POINT * totalPoints;
  // Nanome allocates totalPoints * 24 + 12 zero-filled indices (RibbonBuilder.cs:206); the last point writes only the
  // 12 cap indices, leaving 4 degenerate (0,0,0) triangles. We size exactly and omit them.
  let indexCount = TUBE_INDICES_PER_POINT * totalPoints - TUBE_CAP_INDICES;
  if (segment.nucleic && segment.baseRings) {
    for (let i = 0; i < n; i++) {
      const ring = segment.baseRings[i];
      if (!ring || ring.length === 0) continue;
      vertexCount += BASE_STICK_VERTS;
      indexCount += BASE_STICK_INDICES;
      if (ring.length === PURINE_RING_FLOATS) { vertexCount += PURINE_SLAB_VERTS; indexCount += PURINE_SLAB_INDICES; }
      else { vertexCount += PYRIMIDINE_SLAB_VERTS; indexCount += PYRIMIDINE_SLAB_INDICES; }
    }
  }
  return { vertexCount, indexCount };
}

const EMPTY: RibbonMeshBuffers = Object.freeze({
  position: new Float32Array(0), normal: new Float32Array(0), color: new Float32Array(0),
  residueIndex: new Uint32Array(0), index: new Uint32Array(0), vertexCount: 0, triangleCount: 0,
}) as RibbonMeshBuffers;

/** math.normalizesafe(x) written into out[o..o+2]. */
function normalizeSafeInto(out: Float32Array | Float64Array, o: number, x: number, y: number, z: number): void {
  const len = x * x + y * y + z * z;
  if (len > FLT_MIN_NORMAL) {
    const s = 1 / Math.sqrt(len);
    out[o] = x * s; out[o + 1] = y * s; out[o + 2] = z * s;
  } else {
    out[o] = 0; out[o + 1] = 0; out[o + 2] = 0;
  }
}

/**
 * Build one tube mesh (plus nucleic base slabs) for one continuous segment.
 * Port of RibbonBuilder.CreateSegmentMesh (RibbonBuilder.cs:120-302) with the flat-strip path omitted
 * (disabled at every quality, RenderingQualityConfig.cs:13-16).
 */
export function buildRibbon(segment: RibbonSegment, options: RibbonBuildOptions): RibbonMeshBuffers {
  const n = segment.residueIndex.length;
  if (n < 2) return EMPTY; // RibbonBuilder.cs:139-142
  const steps = options.stepsPerResidue;
  const quirk = options.reproduceSheetSmoothingQuirk ?? RIBBON.reproduceSheetSmoothingQuirk;
  const invSteps = 1 / steps; // InvSplinePoints (RibbonBuilder.cs:144)
  const totalPoints = steps * n;

  const p1 = segment.p1, p2 = segment.p2, style = segment.style, colors = segment.color;

  // ---- FillGVectorJob (RibbonBuilder.cs:320-347) + Gvec[0] = Gvec[1] (:173) ----
  const gvec = new Float64Array(3 * n);
  for (let index = 1; index < n; index++) {
    const last = index - 1;
    const ax = p1[3 * index]! - p1[3 * last]!, ay = p1[3 * index + 1]! - p1[3 * last + 1]!, az = p1[3 * index + 2]! - p1[3 * last + 2]!; // lastPos1ToPos1
    const bx = p2[3 * last]! - p1[3 * last]!, by = p2[3 * last + 1]! - p1[3 * last + 1]!, bz = p2[3 * last + 2]! - p1[3 * last + 2]!; // lastPos1ToLastPos2
    // lastResiduePeptidePlane = cross(a, b)
    const px = ay * bz - az * by, py = az * bx - ax * bz, pz = ax * by - ay * bx;
    // normal = cross(plane, a)
    let nx = py * az - pz * ay, ny = pz * ax - px * az, nz = px * ay - py * ax;
    const gx = gvec[3 * last]!, gy = gvec[3 * last + 1]!, gz = gvec[3 * last + 2]!;
    if (nx * gx + ny * gy + nz * gz < 0) { nx = -nx; ny = -ny; nz = -nz; } // correctedNormal (:341)
    normalizeSafeInto(gvec, 3 * index, gx + nx, gy + ny, gz + nz);          // :342
  }
  gvec[0] = gvec[3]!; gvec[1] = gvec[4]!; gvec[2] = gvec[5]!;                // _pNormals[0] = _pNormals[1] (:173)

  // ---- ControlPointsInitializationJob (RibbonBuilder.cs:350-422) ----
  const controlPoints = new Float64Array(3 * n);
  const widthHeight = new Float64Array(2 * n);
  for (let index = 0; index < n; index++) {
    const last = Math.max(0, index - 1);
    const x = p1[3 * index]!, y = p1[3 * index + 1]!, z = p1[3 * index + 2]!;
    controlPoints[3 * index] = x; controlPoints[3 * index + 1] = y; controlPoints[3 * index + 2] = z;
    const ss = style[index];
    switch (ss) {
      case CartoonStyle.Helix:
      case CartoonStyle.Helix3:
      case CartoonStyle.Helix5:
      case CartoonStyle.NucleicHelix:
        widthHeight[2 * index] = HELIX_HALF_WIDTH; widthHeight[2 * index + 1] = HELIX_HALF_HEIGHT; // :386
        break;
      case CartoonStyle.Sheet: {
        widthHeight[2 * index] = HELIX_HALF_WIDTH; widthHeight[2 * index + 1] = HELIX_HALF_HEIGHT; // :392
        const head = index + 1 >= n || style[index + 1] !== CartoonStyle.Sheet;                     // :395-406
        if (head) widthHeight[2 * index] = -ARROW_HALF_WIDTH;                                       // :410
        // :394 declares nextPosition1 = position1 and never advances it, so the smoothing is (3p + last)/4.
        // With the quirk off we use the next residue's position, the evident intent (2p + next + last)/4.
        const next = quirk ? index : Math.min(n - 1, index + 1);
        controlPoints[3 * index] = (x + x + p1[3 * next]! + p1[3 * last]!) * 0.25;                  // :413
        controlPoints[3 * index + 1] = (y + y + p1[3 * next + 1]! + p1[3 * last + 1]!) * 0.25;
        controlPoints[3 * index + 2] = (z + z + p1[3 * next + 2]! + p1[3 * last + 2]!) * 0.25;
        break;
      }
      default:
        widthHeight[2 * index] = COIL_HALF_SIZE; widthHeight[2 * index + 1] = COIL_HALF_SIZE;       // :418
        break;
    }
  }

  // ---- ComputeWidthHeightJob (RibbonBuilder.cs:425-589) ----
  const splinePoints = new Float64Array(3 * totalPoints);
  const splineNormals = new Float64Array(3 * totalPoints);
  const splineColors = new Float64Array(3 * totalPoints);
  const splineWidthHeight = new Float64Array(2 * totalPoints);
  const coilPerPoint = new Float64Array(totalPoints);
  const m = new Float64Array(12); // 4 rows x xyz (float4x4 matrix rows, :470-493)
  for (let index = 0; index < n; index++) {
    const last = Math.max(0, index - 1);
    const next = Math.min(n - 1, index + 1);
    let c1 = Math.max(0, index - 2), c2 = last, c3 = index, c4 = next;
    const afterNext = Math.min(n - 1, index + 2);
    if (style[index] === CartoonStyle.NucleicHelix) { c1 = c2; c2 = c3; c3 = c4; c4 = afterNext; } // :462-468
    for (let i = 0; i < 4; i++) {
      const row = BSPLINE[i]!;
      for (let k = 0; k < 3; k++) {
        m[3 * i + k] = row[0] * controlPoints[3 * c1 + k]! + row[1] * controlPoints[3 * c2 + k]! +
          row[2] * controlPoints[3 * c3 + k]! + row[3] * controlPoints[3 * c4 + k]!;
      }
    }
    const styleHere = style[index], styleLast = style[last], styleNext = style[next];
    for (let j = 0; j < steps; j++) {
      const v = j * invSteps, vv = 1 - v;
      const o = index * steps + j;
      for (let k = 0; k < 3; k++) {
        splinePoints[3 * o + k] = v * (v * (v * m[k]! + m[3 + k]!) + m[6 + k]!) + m[9 + k]!;       // :499
        splineColors[3 * o + k] = colors[3 * last + k]! + (colors[3 * index + k]! - colors[3 * last + k]!) * v; // math.lerp (:504)
      }
      normalizeSafeInto(splineNormals, 3 * o,                                                        // :503
        vv * gvec[3 * last]! + v * gvec[3 * index]!,
        vv * gvec[3 * last + 1]! + v * gvec[3 * index + 1]!,
        vv * gvec[3 * last + 2]! + v * gvec[3 * index + 2]!);
      if (styleHere !== CartoonStyle.Coil) coilPerPoint[o] = 0;        // :506-508
      else if (styleHere !== styleLast) coilPerPoint[o] = v;           // :510-513 "End of coil"
      else if (styleHere !== styleNext) coilPerPoint[o] = vv;          // :515-518 "Start of coil"
      else coilPerPoint[o] = 1;                                        // :520-523
    }

    // width / height per point (:528-587)
    let wm1 = widthHeight[2 * last]!, w = widthHeight[2 * index]!, wp1 = widthHeight[2 * next]!;
    const hm1 = widthHeight[2 * last + 1]!, h = widthHeight[2 * index + 1]!;
    const base = index * steps;
    if (w >= 0 && wm1 >= 0) {
      for (let j = 0; j < steps; j++) {
        const v = j * invSteps, vv = 1 - v;
        splineWidthHeight[2 * (base + j)] = vv * wm1 + v * w;
        splineWidthHeight[2 * (base + j) + 1] = vv * hm1 + v * h;
      }
    } else {
      if (wp1 < 0) wp1 = -wp1;
      const half = Math.floor(steps / 2); // SplineSteps / 2 (int division)
      if (w < 0) {
        // this residue is the arrow head: previous width across its first half, then taper from the head width to the next residue's
        w = -w;
        for (let j = 0; j < half; j++) {
          const v = j * invSteps;
          splineWidthHeight[2 * (base + j)] = wm1;
          splineWidthHeight[2 * (base + j) + 1] = (1 - v) * hm1 + v * h;
        }
        for (let j = half; j < steps; j++) {
          const v = j * invSteps;
          const nv = (j - steps / 2) * invSteps; // (j - SplineSteps / 2.0f) * InvSplinePoints (:564)
          splineWidthHeight[2 * (base + j)] = (1 - nv) * w + nv * wp1;
          splineWidthHeight[2 * (base + j) + 1] = (1 - v) * hm1 + v * h;
        }
      } else {
        // the previous residue was the arrow head: finish its taper across this residue's first half
        wm1 = -wm1;
        for (let j = 0; j < half; j++) {
          const v = j * invSteps;
          const nv = (j + (steps - steps / 2)) * invSteps; // (j + (SplineSteps - SplineSteps / 2.0f)) * InvSplinePoints (:576)
          splineWidthHeight[2 * (base + j)] = (1 - nv) * wm1 + nv * w;
          splineWidthHeight[2 * (base + j) + 1] = (1 - v) * hm1 + v * h;
        }
        for (let j = half; j < steps; j++) {
          const v = j * invSteps;
          splineWidthHeight[2 * (base + j)] = w;
          splineWidthHeight[2 * (base + j) + 1] = (1 - v) * hm1 + v * h;
        }
      }
    }
  }

  // ---- allocate exactly ----
  const sizes = ribbonBufferSizes(segment, options);
  const position = new Float32Array(3 * sizes.vertexCount);
  const normal = new Float32Array(3 * sizes.vertexCount);
  const color = new Float32Array(3 * sizes.vertexCount);
  const residueIndex = new Uint32Array(sizes.vertexCount);
  const index = new Uint32Array(sizes.indexCount);

  // ---- GenerateMeshJob (RibbonBuilder.cs:592-716) ----
  let idT = 0;
  for (let i = 0; i < totalPoints; i++) {
    const idV = i * 4;
    // forward vector (:620-628)
    let fx = 0, fy = 0, fz = 0;
    if (i < totalPoints - 1) {
      fx = splinePoints[3 * i]! - splinePoints[3 * i + 3]!; fy = splinePoints[3 * i + 1]! - splinePoints[3 * i + 4]!; fz = splinePoints[3 * i + 2]! - splinePoints[3 * i + 5]!;
    } else if (i > 0) {
      fx = splinePoints[3 * i - 3]! - splinePoints[3 * i]!; fy = splinePoints[3 * i - 2]! - splinePoints[3 * i + 1]!; fz = splinePoints[3 * i - 1]! - splinePoints[3 * i + 2]!;
    }
    const nx = splineNormals[3 * i]!, ny = splineNormals[3 * i + 1]!, nz = splineNormals[3 * i + 2]!;
    // upV = normalizesafe(cross(normalV, frontV)) (:631)
    let ux = ny * fz - nz * fy, uy = nz * fx - nx * fz, uz = nx * fy - ny * fx;
    const ul = ux * ux + uy * uy + uz * uz;
    if (ul > FLT_MIN_NORMAL) { const s = 1 / Math.sqrt(ul); ux *= s; uy *= s; uz *= s; } else { ux = 0; uy = 0; uz = 0; }

    const px = splinePoints[3 * i]!, py = splinePoints[3 * i + 1]!, pz = splinePoints[3 * i + 2]!;
    const w = splineWidthHeight[2 * i]!, h = splineWidthHeight[2 * i + 1]!;
    const coil = coilPerPoint[i]!;
    const cr = splineColors[3 * i]!, cg = splineColors[3 * i + 1]!, cb = splineColors[3 * i + 2]!;
    const resIdx = segment.residueIndex[Math.floor(i / steps)]!;
    // v1 = P + up*h + n*w, v2 = P + up*h - n*w, v3 = P - up*h + n*w, v4 = P - up*h - n*w (:633-641)
    for (let k = 0; k < 4; k++) {
      const su = k < 2 ? 1 : -1, sn = (k & 1) === 0 ? 1 : -1;
      const vo = 3 * (idV + k);
      position[vo] = px + ux * h * su + nx * w * sn;
      position[vo + 1] = py + uy * h * su + ny * w * sn;
      position[vo + 2] = pz + uz * h * su + nz * w * sn;
      // Normals[idV+k] = ±upV ± normalV * CoilPerPoint (:648-651). Nanome leaves them un-normalised and lets URP Lit
      // normalise per pixel; we normalise here so the direction is identical and the buffer is unit length.
      normalizeSafeInto(normal, vo, ux * su + nx * coil * sn, uy * su + ny * coil * sn, uz * su + nz * coil * sn);
      // Nanome quantises to Color32 via (byte)(c * 255) (:295); the float lerp is kept here, the renderer may quantise.
      color[vo] = cr; color[vo + 1] = cg; color[vo + 2] = cb;
      residueIndex[idV + k] = resIdx;
    }

    if (i === totalPoints - 1) {
      // End cap (:660-667) then start cap (:669-676)
      index[idT++] = i * 4; index[idT++] = i * 4 + 1; index[idT++] = i * 4 + 3;
      index[idT++] = i * 4 + 2; index[idT++] = i * 4; index[idT++] = i * 4 + 3;
      index[idT++] = 0; index[idT++] = 3; index[idT++] = 1;
      index[idT++] = 0; index[idT++] = 2; index[idT++] = 3;
      break;
    }
    const a = i * 4, b = (i + 1) * 4;
    // Top (:680-687)
    index[idT++] = a; index[idT++] = a + 1; index[idT++] = b;
    index[idT++] = a + 1; index[idT++] = b + 1; index[idT++] = b;
    // Bottom (:689-696)
    index[idT++] = a + 2; index[idT++] = b + 2; index[idT++] = a + 3;
    index[idT++] = a + 3; index[idT++] = b + 2; index[idT++] = b + 3;
    // Left (:698-705)
    index[idT++] = a + 2; index[idT++] = a; index[idT++] = b + 2;
    index[idT++] = a; index[idT++] = b; index[idT++] = b + 2;
    // Right (:707-714)
    index[idT++] = a + 1; index[idT++] = a + 3; index[idT++] = b + 1;
    index[idT++] = a + 3; index[idT++] = b + 3; index[idT++] = b + 1;
  }

  // ---- AddNucleicBases (RibbonBuilder.cs:835-1229), only when a residue is NucleicHelix (:298-301) ----
  let vBase = TUBE_VERTS_PER_POINT * totalPoints;
  if (segment.nucleic && segment.baseRings) {
    const half = Math.floor(steps / 2);
    for (let i = 0; i < n; i++) {
      const ring: Float32Array | undefined = segment.baseRings[i];
      if (!ring || ring.length === 0) continue; // "No ring found" (:855-858)
      const isPurine: boolean = ring.length === PURINE_RING_FLOATS; // Ring2 present (:958-959)
      const resIdx = segment.residueIndex[i]!;
      const cr = colors[3 * i]!, cg = colors[3 * i + 1]!, cb = colors[3 * i + 2]!;

      // ring1 plane normal = normalizesafe(cross(Atom3 - Atom1, Atom5 - Atom1)) (GetPlaneNormal :1231-1240)
      const r1x = ring[0]!, r1y = ring[1]!, r1z = ring[2]!;
      const e1x = ring[6]! - r1x, e1y = ring[7]! - r1y, e1z = ring[8]! - r1z;
      const e2x = ring[12]! - r1x, e2y = ring[13]! - r1y, e2z = ring[14]! - r1z;
      const rn = new Float64Array(3);
      normalizeSafeInto(rn, 0, e1y * e2z - e1z * e2y, e1z * e2x - e1x * e2z, e1x * e2y - e1y * e2x);
      const rnx = rn[0]!, rny = rn[1]!, rnz = rn[2]!;

      // start = spline point at the residue's midpoint (:868)
      const sp = i * steps + half;
      let sx = splinePoints[3 * sp]!, sy = splinePoints[3 * sp + 1]!, sz = splinePoints[3 * sp + 2]!;
      const dx = r1x - sx, dy = r1y - sy, dz = r1z - sz; // startToFirst (:869)
      const u = new Float64Array(3), v = new Float64Array(3), d = new Float64Array(3);
      normalizeSafeInto(u, 0, dy * rnz - dz * rny, dz * rnx - dx * rnz, dx * rny - dy * rnx);                   // u = normalize(cross(startToFirst, ring1Normal)) (:871)
      normalizeSafeInto(v, 0, dy * u[2]! - dz * u[1]!, dz * u[0]! - dx * u[2]!, dx * u[1]! - dy * u[0]!);       // v = normalize(cross(startToFirst, u)) (:872)
      normalizeSafeInto(d, 0, dx, dy, dz);
      sx += d[0]! * 0.05; sy += d[1]! * 0.05; sz += d[2]! * 0.05;                                               // start += normalize(startToFirst) * 0.05 (:874)

      const vertexIndex = vBase;
      // v1..v4 = start ± u*height ± v*height (:875-878), normals toward start (:890-893)
      for (let k = 0; k < 4; k++) {
        const su = k < 2 ? 1 : -1, sv = (k & 1) === 0 ? 1 : -1;
        const vx = sx + u[0]! * BASE_HEIGHT * su + v[0]! * BASE_HEIGHT * sv;
        const vy = sy + u[1]! * BASE_HEIGHT * su + v[1]! * BASE_HEIGHT * sv;
        const vz = sz + u[2]! * BASE_HEIGHT * su + v[2]! * BASE_HEIGHT * sv;
        const vo = 3 * (vertexIndex + k);
        position[vo] = vx; position[vo + 1] = vy; position[vo + 2] = vz;
        normalizeSafeInto(normal, vo, vx - sx, vy - sy, vz - sz);
        // far end: v += startToFirst * 1.05 (:895-898), normals toward the first ring atom (:905-908)
        const fx = vx + dx * 1.05, fy = vy + dy * 1.05, fz = vz + dz * 1.05;
        const fo = 3 * (vertexIndex + 4 + k);
        position[fo] = fx; position[fo + 1] = fy; position[fo + 2] = fz;
        normalizeSafeInto(normal, fo, fx - r1x, fy - r1y, fz - r1z);
      }
      for (let k = 0; k < 8; k++) {
        const vo = 3 * (vertexIndex + k);
        color[vo] = cr; color[vo + 1] = cg; color[vo + 2] = cb;
        residueIndex[vertexIndex + k] = resIdx;
      }
      // stick faces (:911-948)
      for (const t of STICK_TRIANGLES) index[idT++] = vertexIndex + t;
      vBase += BASE_STICK_VERTS;

      // ring slabs (:957-1219): top = atom + n*height with normal n, bottom = atom - n*height with normal -n
      const slabBase = vBase;
      const count: number = isPurine ? 9 : 6;
      // purine: v1..v5 = Ring1.Atom1..5, v6..v9 = Ring2.Atom2..5 (:966-975); pyrimidine: v1..v6 = Ring1.Atom1..6 (:1133-1138)
      for (let k = 0; k < count; k++) {
        const atom = isPurine && k >= 5 ? k + 1 : k; // ring2 starts at flat atom 5 (C4, unused); Ring2.Atom2..5 are flat atoms 6..9
        const ax = ring[3 * atom]!, ay = ring[3 * atom + 1]!, az = ring[3 * atom + 2]!;
        const top = 3 * (slabBase + k), bottom = 3 * (slabBase + count + k);
        position[top] = ax + rnx * BASE_HEIGHT; position[top + 1] = ay + rny * BASE_HEIGHT; position[top + 2] = az + rnz * BASE_HEIGHT;
        normal[top] = rnx; normal[top + 1] = rny; normal[top + 2] = rnz;
        position[bottom] = ax - rnx * BASE_HEIGHT; position[bottom + 1] = ay - rny * BASE_HEIGHT; position[bottom + 2] = az - rnz * BASE_HEIGHT;
        normal[bottom] = -rnx; normal[bottom + 1] = -rny; normal[bottom + 2] = -rnz;
      }
      for (let k = 0; k < 2 * count; k++) {
        const vo = 3 * (slabBase + k);
        color[vo] = cr; color[vo + 1] = cg; color[vo + 2] = cb;
        residueIndex[slabBase + k] = resIdx;
      }
      const tris = isPurine ? PURINE_TRIANGLES : PYRIMIDINE_TRIANGLES;
      for (const t of tris) index[idT++] = slabBase + t;
      vBase += 2 * count;
    }
  }

  if (idT !== index.length || vBase !== sizes.vertexCount) {
    throw new Error(`ribbon buffer size mismatch: wrote ${idT}/${index.length} indices, ${vBase}/${sizes.vertexCount} vertices`);
  }
  return { position, normal, color, residueIndex, index, vertexCount: sizes.vertexCount, triangleCount: index.length / 3 };
}

/** Stick index table, relative to its first vertex (RibbonBuilder.cs:911-948). */
const STICK_TRIANGLES: readonly number[] = [
  0, 4, 1, 1, 4, 5,       // Face 1
  3, 1, 7, 1, 5, 7,       // Face 2
  2, 3, 6, 3, 7, 6,       // Face 3
  0, 2, 4, 2, 6, 4,       // Face 4
  4, 6, 7, 4, 7, 5,       // far cap
];

/** Purine slab index table, 9 top vertices then 9 bottom (RibbonBuilder.cs:1018-1129). */
const PURINE_TRIANGLES: readonly number[] = [
  // sides (:1018-1081)
  0, 9, 1, 9, 10, 1,
  1, 10, 5, 5, 10, 14,
  5, 14, 15, 5, 15, 6,
  6, 15, 16, 6, 16, 7,
  7, 16, 17, 7, 17, 8,
  8, 17, 11, 8, 11, 2,
  2, 11, 3, 3, 11, 12,
  3, 12, 4, 4, 12, 13,
  4, 13, 0, 0, 13, 9,
  // top (:1083-1105)
  0, 3, 4, 0, 2, 3, 0, 1, 2, 1, 8, 2, 1, 5, 8, 5, 7, 8, 5, 6, 7,
  // bottom (:1107-1129)
  9, 13, 12, 9, 12, 11, 9, 11, 10, 10, 11, 17, 10, 17, 14, 14, 17, 16, 14, 16, 15,
];

/** Pyrimidine slab index table, 6 top vertices then 6 bottom (RibbonBuilder.cs:1169-1218). */
const PYRIMIDINE_TRIANGLES: readonly number[] = (() => {
  const t: number[] = [];
  for (let j = 0; j < 5; j++) t.push(0 + j, 6 + j, 1 + j, 6 + j, 7 + j, 1 + j); // sides (:1171-1179)
  t.push(5, 11, 0, 0, 11, 6);                                                  // closing side (:1181-1186)
  t.push(0, 1, 2, 0, 2, 3, 0, 3, 4, 0, 4, 5);                                  // top (:1190-1203)
  t.push(6, 8, 7, 6, 9, 8, 6, 10, 9, 6, 11, 10);                               // bottom (:1205-1218)
  return t;
})();
